// Phase 5.7 · 방송 결과 리포트 모달
// - 기존 /api/seller/live/broadcasts/{sid}/report 응답만 시각화 (통계 계산 로직 X)
// - PC/Mobile 반응형
import React, { useEffect, useState } from 'react';
import { X, Radio, Clock, Eye, MessageCircle, Heart, Share2, UserPlus, Gift, ShoppingCart, TrendingUp, Users } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const fmtDuration = (sec) => {
  if (!sec || sec <= 0) return '-';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}시간 ${m}분`;
  if (m > 0) return `${m}분 ${s}초`;
  return `${s}초`;
};

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 bg-white dark:bg-[#0f0f0f]" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">
      <Icon size={11} /> {label}
    </div>
    <p className={`text-lg font-black tracking-tight ${accent ? 'text-[#FF3B30]' : ''}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
      {value}
    </p>
  </div>
);

export default function LiveReportModal({ scheduleId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !scheduleId) return;
    setLoading(true); setError(null); setData(null);
    api.get(`/seller/live/broadcasts/${scheduleId}/report`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || '리포트를 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [open, scheduleId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="live-report-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-[#141414] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-4 border-b border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#FF3B30]" />
            <h2 className="text-lg font-black" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="report-title">라이브 결과 리포트</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10" data-testid="report-close">
            <X size={18} />
          </button>
        </div>

        {loading && <p className="p-10 text-center text-sm text-gray-500" data-testid="report-loading">불러오는 중…</p>}
        {error && <p className="p-10 text-center text-sm text-red-500" data-testid="report-error">{error}</p>}
        {data && (
          <div className="p-4 sm:p-6 space-y-6">
            {/* 방송 정보 */}
            <div data-testid="report-broadcast-info">
              <p className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-1">방송</p>
              <h3 className="text-xl font-black" data-testid="report-broadcast-title">{data.title || '(제목 없음)'}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {data.scheduled_at && <>예정: {format(new Date(data.scheduled_at), 'yyyy년 M월 d일 HH:mm', { locale: ko })}</>}
                {data.started_at && <> · 시작: {format(new Date(data.started_at), 'HH:mm', { locale: ko })}</>}
                {data.ended_at && <> · 종료: {format(new Date(data.ended_at), 'HH:mm', { locale: ko })}</>}
              </p>
            </div>

            {/* 핵심 지표 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="report-key-metrics">
              <Stat icon={Clock}         label="방송 시간"       value={fmtDuration(data.duration_sec)} testId="report-stat-duration" />
              <Stat icon={Eye}           label="최고 시청자"     value={(data.engagement?.peak_viewers || 0).toLocaleString('ko-KR')} testId="report-stat-peak" />
              <Stat icon={MessageCircle} label="댓글 수"         value={(data.engagement?.comment_count || 0).toLocaleString('ko-KR')} testId="report-stat-comments" />
              <Stat icon={ShoppingCart}  label="주문 수"         value={(data.totals?.orders_count || 0).toLocaleString('ko-KR')} testId="report-stat-orders" accent />
              <Stat icon={Heart}         label="좋아요"          value={(data.engagement?.like_count || 0).toLocaleString('ko-KR')} testId="report-stat-likes" />
              <Stat icon={UserPlus}      label="팔로우"          value={(data.engagement?.follow_count || 0).toLocaleString('ko-KR')} testId="report-stat-follows" />
              <Stat icon={Share2}        label="공유"            value={(data.engagement?.share_count || 0).toLocaleString('ko-KR')} testId="report-stat-shares" />
              <Stat icon={TrendingUp}    label="판매 금액"       value={won(data.totals?.sold_amount)} testId="report-stat-amount" accent />
            </div>

            {/* Gift */}
            {(data.engagement?.gift_count || 0) > 0 && (
              <div data-testid="report-gifts">
                <p className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-2 inline-flex items-center gap-1"><Gift size={11}/> Gift · 다이아 {data.engagement.gift_diamond_total.toLocaleString('ko-KR')}</p>
                <div className="rounded-lg border border-black/10 dark:border-white/10 divide-y divide-black/5 dark:divide-white/5">
                  {(data.top_gifters || []).length === 0 ? (
                    <p className="p-3 text-xs text-gray-500">Top gifter 데이터 없음</p>
                  ) : (
                    data.top_gifters.map((g, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`report-gifter-${i}`}>
                        <span className="w-5 h-5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                        <span className="font-bold flex-1 truncate">{g.display_name}</span>
                        <span className="text-xs text-gray-500">{g.gift_count}회</span>
                        <span className="text-xs font-black text-[#FF3B30]">💎 {(g.diamond_total || 0).toLocaleString('ko-KR')}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Top 상품 */}
            <div data-testid="report-top-products">
              <p className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-2 inline-flex items-center gap-1"><TrendingUp size={11}/> Top 판매 상품</p>
              {(data.top_products || []).length === 0 ? (
                <p className="p-3 text-xs text-gray-500 border border-dashed border-black/10 dark:border-white/10 rounded-lg text-center">판매된 상품이 없습니다.</p>
              ) : (
                <div className="rounded-lg border border-black/10 dark:border-white/10 divide-y divide-black/5 dark:divide-white/5">
                  {data.top_products.map((p, i) => (
                    <div key={p.product_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`report-top-product-${i}`}>
                      <span className="w-6 h-6 rounded-full bg-black text-white text-xs font-black flex items-center justify-center shrink-0">{p.slot_number}</span>
                      <span className="font-bold flex-1 truncate">{p.name || p.product_id}</span>
                      <span className="text-xs text-gray-500">{p.sold_qty}개</span>
                      <span className="text-sm font-black text-[#FF3B30]">{won(p.sold_amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 전체 상품 성과 */}
            <div data-testid="report-product-summary">
              <p className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-2 inline-flex items-center gap-1"><Radio size={11}/> 전체 상품 성과 ({data.products?.length || 0}건)</p>
              <div className="rounded-lg border border-black/10 dark:border-white/10 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-black/5 dark:bg-white/5">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">상품</th>
                      <th className="px-2 py-2 text-right">소개</th>
                      <th className="px-2 py-2 text-right">후보</th>
                      <th className="px-2 py-2 text-right">확정</th>
                      <th className="px-2 py-2 text-right">주문</th>
                      <th className="px-2 py-2 text-right">수량</th>
                      <th className="px-2 py-2 text-right">금액</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/5">
                    {(data.products || []).map((p) => (
                      <tr key={p.product_id} data-testid={`report-row-${p.product_id}`}>
                        <td className="px-2 py-2 font-black">{p.slot_number}</td>
                        <td className="px-2 py-2 truncate max-w-[200px]">{p.name || p.product_id}</td>
                        <td className="px-2 py-2 text-right">{p.is_introduced ? <span className="text-emerald-600 font-black">✓</span> : '-'}</td>
                        <td className="px-2 py-2 text-right">{p.candidates_pending}</td>
                        <td className="px-2 py-2 text-right">{p.candidates_confirmed}</td>
                        <td className="px-2 py-2 text-right">{p.orders_count}</td>
                        <td className="px-2 py-2 text-right">{p.sold_qty}</td>
                        <td className="px-2 py-2 text-right font-black text-[#FF3B30]">{won(p.sold_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 세션 (플랫폼별) */}
            {(data.sessions || []).length > 0 && (
              <div data-testid="report-sessions">
                <p className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-2 inline-flex items-center gap-1"><Users size={11}/> LIVE 세션 ({data.sessions.length}개)</p>
                <div className="rounded-lg border border-black/10 dark:border-white/10 divide-y divide-black/5 dark:divide-white/5">
                  {data.sessions.map((s) => (
                    <div key={s.live_session_id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`report-session-${s.live_session_id}`}>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                        s.status === 'ended' ? 'bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-gray-300'
                        : 'bg-[#FF3B30]/10 text-[#FF3B30]'}`}>
                        {(s.platform || '').toUpperCase()} · {s.status === 'ended' ? '종료' : 'LIVE'}
                      </span>
                      <span className="text-gray-500 text-[10px] truncate">{s.live_session_id.slice(0, 20)}…</span>
                      <span className="ml-auto text-[10px] text-gray-500">
                        댓글 {s.comment_count || 0} · Gift {s.gift_count || 0} · Peak {s.peak_viewers || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
